import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/AppLayout";
import { ApprovalThread } from "@/components/ApprovalThread";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Undo2, ExternalLink, Loader2, FileText, Truck, Inbox, Factory, Check, CheckCheck } from "lucide-react";
import { fmtCurrencyExact } from "@/lib/constants";

const fmtDT = (s) => (s ? new Date(s).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—");
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");
const EVAL_CRITERIA = [
  ["quality", "Quality systems & certification"],
  ["insurance", "Insurances current & adequate"],
  ["capability", "Capability, capacity & lead times"],
  ["whs", "WHS compliance"],
  ["commercial", "Commercial terms acceptable"],
];
const KIND_BADGE = {
  quote: ["QUOTE", "border-cyan-300 bg-cyan-50 text-cyan-700", FileText],
  po: ["PO", "border-violet-300 bg-violet-50 text-violet-700", Truck],
  supplier: ["SUPPLIER", "border-violet-300 bg-violet-50 text-violet-700", Factory],
};
const DOC_KIND_LABEL = {
  insurance_pl: "Public liability", insurance_wc: "Workers comp", licences: "Licence",
  quality_cert: "Quality cert", agreement: "Signed agreement",
};
const endpointBase = (kind) => (kind === "quote" ? "quotes" : kind === "po" ? "orders" : "suppliers");
const bizDaysWaiting = (iso) => {
  if (!iso) return 0;
  const s = new Date(iso);
  let d = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const now = new Date();
  const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let n = 0, guard = 0;
  while (d < t && guard < 400) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) n++;
    guard++;
  }
  return n;
};

export default function Approvals() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [rc, setRc] = useState(null);
  const [rcComment, setRcComment] = useState("");
  const [ev, setEv] = useState(null); // supplier approve dialog
  const [criteria, setCriteria] = useState({});
  const [evNote, setEvNote] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["approvals-pending"],
    queryFn: async () => (await api.get("/approvals/pending")).data,
    refetchInterval: 60000,
  });
  const items = data?.items || [];
  const unreadTotal = data?.unread_total || 0;
  const isApprover = data?.is_approver ?? !!(user?.quote_approver || user?.po_approver || user?.supplier_approver || user?.role === "super_admin");

  const markRead = useMutation({
    mutationFn: async ({ kind, id }) => api.post(`/approvals/${kind}/${id}/mark-read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvals-pending"] }),
  });
  const markAllRead = useMutation({
    mutationFn: async () => api.post("/approvals/mark-all-read"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["approvals-pending"] }); toast.success("All approval updates marked as read"); },
  });

  const act = useMutation({
    mutationFn: async ({ item, action, body }) =>
      (await api.post(`/${endpointBase(item.kind)}/${item.id}/${action}`, body || {})).data,
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["approvals-pending"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setRc(null); setRcComment(""); setEv(null);
      if (vars.action === "approve") toast.success(vars.item.kind === "supplier" ? `${vars.item.title} approved as a supplier` : `${vars.item.number} approved`);
      else toast.success(vars.item.kind === "supplier" ? "Sent back — the supplier's link has been re-opened with your note" : "Sent back for changes — the submitter has been notified");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Action failed"),
  });

  const openSupplierApprove = (it) => {
    setCriteria(Object.fromEntries(EVAL_CRITERIA.map(([k]) => [k, true])));
    setEvNote("");
    setEv(it);
  };
  const allPass = EVAL_CRITERIA.every(([k]) => criteria[k]);

  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approvals"
        subtitle={isApprover
          ? `${items.length} item${items.length !== 1 ? "s" : ""} waiting on you`
          : `${items.length} of your submission${items.length !== 1 ? "s" : ""} awaiting approval`}
        breadcrumb={["Operations", "Approvals"]} />
      <div className="px-6 lg:px-10 py-6 max-w-5xl space-y-4">
        {unreadTotal > 0 && (
          <div className="flex items-center justify-end gap-2" data-testid="approvals-unread-bar">
            <span data-testid="approvals-unread-total" className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 whitespace-nowrap">
              {unreadTotal} unread update{unreadTotal === 1 ? "" : "s"}
            </span>
            <Button size="sm" variant="outline" data-testid="approvals-mark-all-read" onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending} className="h-9 rounded-sm text-xs whitespace-nowrap">
              <CheckCheck size={14} className="mr-1.5" /> Mark all read
            </Button>
          </div>
        )}
        {isLoading && <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center"><Loader2 size={16} className="animate-spin" /> Loading…</div>}
        {!isLoading && items.length === 0 && (
          <Card className="rounded-sm border-slate-200 industrial-shadow p-10 text-center" data-testid="approvals-empty">
            <Inbox size={28} className="mx-auto text-emerald-500 mb-2" />
            <div className="font-display font-semibold text-slate-900">All clear</div>
            <div className="text-sm text-slate-500 mt-1">{isApprover ? "Nothing is waiting for approval right now." : "You have nothing waiting for approval."}</div>
          </Card>
        )}
        {items.map((it) => {
          const waiting = it.waiting_biz_days ?? bizDaysWaiting(it.submitted_at);
          const [kLabel, kCls, KIcon] = KIND_BADGE[it.kind] || KIND_BADGE.quote;
          const canAct = it.can_act ?? isApprover;
          return (
            <Card key={`${it.kind}-${it.id}`} className={`rounded-sm industrial-shadow p-4 space-y-3 ${it.unread ? "border-sky-300 bg-sky-50/50" : "border-slate-200"}`} data-testid={`approval-item-${it.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {it.unread > 0 && <span className="w-2 h-2 rounded-full bg-sky-500 shrink-0" data-testid={`approval-unread-dot-${it.id}`} />}
                    <Badge variant="outline" className={`rounded-sm text-[9px] font-bold ${kCls}`}>
                      <KIcon size={9} className="mr-1" />{kLabel}
                    </Badge>
                    {it.kind === "supplier" && it.critical && (
                      <Badge variant="outline" className="rounded-full text-[9px] font-bold border-rose-300 bg-rose-50 text-rose-700">CRITICAL</Badge>
                    )}
                    {it.kind !== "supplier" && <span className="font-mono font-bold text-sm text-slate-900">{it.number}</span>}
                    <span className={`text-sm truncate ${it.kind === "supplier" ? "font-semibold text-slate-900" : "text-slate-600"}`}>{it.title}</span>
                    {it.value != null && <span className="font-mono text-sm font-semibold text-slate-900">{fmtCurrencyExact(it.value)}</span>}
                    {it.revision != null && <Badge variant="outline" className="rounded-sm text-[9px] border-slate-300 text-slate-500">REV {it.revision}</Badge>}
                    {it.unread > 0 && <Badge data-testid={`approval-unread-pill-${it.id}`} className="rounded-full text-[9px] bg-amber-400 text-slate-900 border-0 font-bold hover:bg-amber-400">{it.unread} new</Badge>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {it.kind === "supplier"
                      ? <>Onboarding submitted {fmtDT(it.submitted_at)}{it.sent_by_name ? <> · invite sent by <b>{it.sent_by_name}</b></> : null}</>
                      : <>Submitted by <b>{it.submitted_by_name || it.submitted_by || "—"}</b> · {fmtDT(it.submitted_at)}</>}
                    <span className={`ml-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${waiting >= 3 ? "bg-rose-100 text-rose-700" : waiting >= 1 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`} data-testid={`approval-waiting-${it.id}`}>
                      waiting {waiting} business day{waiting !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {it.note && (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-[12.5px] text-slate-700 italic flex items-start gap-2" data-testid={`approval-note-${it.id}`}>
                      <span className="not-italic stamp-label text-amber-700 shrink-0 pt-px">Submitter's note</span>
                      <span>“{it.note}”</span>
                    </div>
                  )}
                  {it.kind === "supplier" && (it.documents || []).length > 0 && (
                    <div className="mt-2 grid sm:grid-cols-2 gap-1 max-w-xl" data-testid={`approval-docs-${it.id}`}>
                      {it.documents.map((d) => (
                        <div key={d.id} className="flex items-center justify-between border border-slate-200 bg-slate-50 rounded-sm px-2.5 py-1.5 text-[11px] text-slate-700">
                          <span className="truncate">📄 {DOC_KIND_LABEL[d.kind] ? `${DOC_KIND_LABEL[d.kind]} — ` : ""}{d.name}{d.insurer ? ` · ${d.insurer}` : ""}</span>
                          {d.expiry && <span className="text-[9px] font-bold text-emerald-700 shrink-0 ml-2">exp {fmtD(d.expiry)}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0 flex-wrap">
                  {it.unread > 0 && (
                    <Button size="sm" variant="ghost" className="rounded-sm h-8 px-2 text-[11px] text-emerald-700" data-testid={`approval-mark-read-${it.id}`}
                      disabled={markRead.isPending} onClick={() => markRead.mutate({ kind: it.kind, id: it.id })} title="Mark as read">
                      <Check size={12} className="mr-1" /> Read
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-sm h-8 text-[11px]" data-testid={`approval-open-${it.id}`}
                    onClick={() => nav(it.link)}>
                    <ExternalLink size={12} className="mr-1" /> Open
                  </Button>
                  {canAct && (
                    <>
                      <Button size="sm" className="rounded-sm h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" disabled={act.isPending}
                        data-testid={`approval-approve-${it.id}`}
                        onClick={() => (it.kind === "supplier" ? openSupplierApprove(it) : act.mutate({ item: it, action: "approve" }))}>
                        <ShieldCheck size={12} className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" className="rounded-sm h-8 text-[11px] bg-rose-600 hover:bg-rose-700 text-white" disabled={act.isPending}
                        data-testid={`approval-changes-${it.id}`}
                        onClick={() => { setRcComment(""); setRc(it); }}>
                        <Undo2 size={12} className="mr-1" /> Request changes
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <ApprovalThread kind={it.kind} itemId={it.id} thread={it.thread} canReply
                onPosted={() => qc.invalidateQueries({ queryKey: ["approvals-pending"] })} />
            </Card>
          );
        })}
      </div>

      <Dialog open={!!rc} onOpenChange={(o) => !o && setRc(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="approvals-request-changes-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Undo2 size={18} className="text-rose-600" /> Request changes — {rc?.kind === "supplier" ? rc?.title : rc?.number}
            </DialogTitle>
          </DialogHeader>
          <div>
            <div className="stamp-label text-slate-600 mb-1">What needs to change? (required)</div>
            <Textarea rows={4} value={rcComment} onChange={(e) => setRcComment(e.target.value)} maxLength={2000}
              className="rounded-sm text-sm" placeholder={rc?.kind === "supplier" ? "Tell the supplier what's missing — their link re-opens for 14 days with your note…" : "Tell the submitter what to fix before resubmitting…"}
              data-testid="approvals-request-changes-comment" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRc(null)} className="rounded-sm">Cancel</Button>
            <Button disabled={act.isPending || rcComment.trim().length < 3}
              onClick={() => act.mutate({ item: rc, action: "request-changes", body: { comment: rcComment.trim() } })}
              className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="approvals-request-changes-send">
              {act.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Undo2 size={14} className="mr-1.5" />}
              Send back for changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ev} onOpenChange={(o) => !o && setEv(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="supplier-eval-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <ShieldCheck size={18} className="text-emerald-600" /> Approve supplier — {ev?.title}
            </DialogTitle>
          </DialogHeader>
          <div>
            <div className="stamp-label text-slate-600 mb-2">Evaluation — supplier approval criteria</div>
            {EVAL_CRITERIA.map(([k, label]) => (
              <div key={k} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-b-0 text-xs text-slate-700">
                <span>{label}</span>
                <div className="flex gap-1">
                  <button type="button" data-testid={`hub-eval-pass-${k}`} onClick={() => setCriteria((c) => ({ ...c, [k]: true }))}
                    className={`px-2.5 py-0.5 rounded-sm text-[10px] font-bold border ${criteria[k] ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-300 text-slate-500"}`}>Pass</button>
                  <button type="button" data-testid={`hub-eval-fail-${k}`} onClick={() => setCriteria((c) => ({ ...c, [k]: false }))}
                    className={`px-2.5 py-0.5 rounded-sm text-[10px] font-bold border ${!criteria[k] ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-slate-300 text-slate-500"}`}>Fail</button>
                </div>
              </div>
            ))}
            <Textarea rows={2} value={evNote} onChange={(e) => setEvNote(e.target.value)} maxLength={2000}
              className="rounded-sm text-xs mt-3" placeholder="Evaluation note (optional)" data-testid="hub-eval-note" />
            {!allPass && <p className="text-[10.5px] text-rose-600 mt-2">A failed criterion blocks approval — use Request changes instead so the supplier can fix it.</p>}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEv(null)} className="rounded-sm">Cancel</Button>
            <Button disabled={act.isPending || !allPass}
              onClick={() => act.mutate({ item: ev, action: "approve", body: { criteria, note: evNote.trim() || null } })}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="hub-eval-approve-btn">
              {act.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <ShieldCheck size={14} className="mr-1.5" />}
              Approve supplier — 12-month review set
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
